import type { AxiosError } from 'axios';

const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT_RE = /^\d+$/;
const LONG_TOKEN_SEGMENT_RE = /^[0-9a-z_-]{16,}$/i;

type RequestConfigWithContext = {
    baseURL?: string;
    url?: string;
    method?: string;
    timeout?: number;
    requestContext?: string;
};

type ErrorPayload = {
    code?: unknown;
    message?: unknown;
    errorCode?: unknown;
    errorMessage?: unknown;
};

export type RequestErrorContext = {
    extra: Record<string, string | number | boolean | null>;
    fingerprint: string[];
};

const toAbsoluteUrl = (baseURL?: string, url?: string) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    if (baseURL) {
        const normalizedBaseURL = baseURL.replace(/\/+$/, '');
        const normalizedUrl = url.replace(/^\/+/, '');
        return `${normalizedBaseURL}/${normalizedUrl}`;
    }

    try {
        return new URL(url, baseURL || 'https://app.allibee.ai').toString();
    } catch {
        return url;
    }
};

const normalizePathSegment = (segment: string) => {
    if (!segment) return segment;
    if (UUID_SEGMENT_RE.test(segment)) return ':uuid';
    if (NUMERIC_SEGMENT_RE.test(segment)) return ':id';
    if (LONG_TOKEN_SEGMENT_RE.test(segment) && !segment.startsWith('v')) return ':token';
    return segment;
};

const getErrorPayload = (data: unknown): ErrorPayload | null => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    return data as ErrorPayload;
};

export const normalizeRequestPath = (requestUrl: string) => {
    if (!requestUrl) return '';

    try {
        const parsed = new URL(requestUrl, 'https://app.allibee.ai');
        const normalizedPathname = parsed.pathname
            .split('/')
            .map(normalizePathSegment)
            .join('/');
        return `${normalizedPathname}${parsed.search ? '?' : ''}`;
    } catch {
        return requestUrl;
    }
};

export const isAbortLikeError = (error: Pick<AxiosError, 'code' | 'name'>) => {
    return error.name === 'AbortError' || error.code === 'ERR_CANCELED';
};

export const isTimeoutLikeError = (error: Pick<AxiosError, 'code' | 'message'>) => {
    return error.code === 'ECONNABORTED' || error.message?.includes('timeout') || false;
};

export const getRequestErrorContext = (error: AxiosError & { config?: RequestConfigWithContext }): RequestErrorContext => {
    const config = (error.config || {}) as RequestConfigWithContext;
    const requestUrl = toAbsoluteUrl(config.baseURL, config.url);
    const normalizedPath = normalizeRequestPath(requestUrl);
    const method = (config.method || 'get').toUpperCase();
    const status = error.response?.status ?? null;
    const payload = getErrorPayload(error.response?.data);
    const backendCode = String(payload?.code || payload?.errorCode || '');
    const backendMessage = String(payload?.message || payload?.errorMessage || '');
    const requestContext = config.requestContext || null;
    const serviceName = normalizedPath.split('/').filter(Boolean)[1] || 'unknown';
    const fingerprint = [
        'axios-request',
        serviceName,
        method,
        normalizedPath || 'unknown-path',
        status ? String(status) : isTimeoutLikeError(error) ? 'timeout' : error.code || 'unknown',
        backendCode || 'no-backend-code',
    ];

    return {
        extra: {
            requestMethod: method,
            requestUrl: requestUrl || null,
            normalizedRequestPath: normalizedPath || null,
            responseStatus: status,
            backendErrorCode: backendCode || null,
            backendErrorMessage: backendMessage || null,
            timeoutMs: config.timeout ?? null,
            requestContext,
            isAuthBootstrap: requestContext === 'auth-bootstrap',
            isRefreshToken: requestContext === 'refresh-token',
        },
        fingerprint,
    };
};
